import { useEffect, useState } from 'react'

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * Lead LOCAL TIME (Lee, 23 Sep 2026) — derived from the lead's country (the
 * WeQuote API field on the deal). Country → the country's main BUSINESS
 * timezone; multi-zone countries (US, AU, CA…) use the dominant commercial
 * one (US → Eastern, AU → Sydney), which is honest enough for "is it a sane
 * hour to ring them" without street-level geo we don't hold.
 * Used on the board cards ("their local time now") and in the booking modal
 * ("what the chosen slot is for them").
 */
export const COUNTRY_TZ: Record<string, string> = {
  GB: 'Europe/London', IE: 'Europe/Dublin', US: 'America/New_York', CA: 'America/Toronto',
  AU: 'Australia/Sydney', NZ: 'Pacific/Auckland', AE: 'Asia/Dubai', SA: 'Asia/Riyadh',
  AT: 'Europe/Vienna', BE: 'Europe/Brussels', CH: 'Europe/Zurich', DE: 'Europe/Berlin',
  DK: 'Europe/Copenhagen', ES: 'Europe/Madrid', FI: 'Europe/Helsinki', FR: 'Europe/Paris',
  GR: 'Europe/Athens', HK: 'Asia/Hong_Kong', IN: 'Asia/Kolkata', IT: 'Europe/Rome',
  NL: 'Europe/Amsterdam', NO: 'Europe/Oslo', PL: 'Europe/Warsaw', PT: 'Europe/Lisbon',
  SE: 'Europe/Stockholm', SG: 'Asia/Singapore', ZA: 'Africa/Johannesburg',
}

export const tzForCountry = (country?: string | null): string =>
  COUNTRY_TZ[String(country || 'GB').toUpperCase()] || 'Europe/London'

/** Short zone label, e.g. "BST", "EDT", "GST" — falls back to the offset. */
export const tzAbbr = (tz: string, at: Date = new Date()): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'short' }).formatToParts(at)
    return parts.find((p) => p.type === 'timeZoneName')?.value || ''
  } catch { return '' }
}

export const timeInTz = (tz: string, at: Date = new Date()): string => {
  try {
    return at.toLocaleTimeString('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit' })
  } catch { return '' }
}

/** With seconds — the live board-card clock. */
export const timeInTzSec = (tz: string, at: Date = new Date()): string => {
  try {
    return at.toLocaleTimeString('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch { return '' }
}

/** True when the two zones show a different wall-clock time right now. */
export const tzDiffers = (a: string, b: string, at: Date = new Date()): boolean =>
  timeInTz(a, at) !== timeInTz(b, at) || tzAbbr(a, at) !== tzAbbr(b, at)

export const viewerTz = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London' } catch { return 'Europe/London' }
}

/** Ticks every 60s — for components that genuinely need a React re-render
 * (e.g. the booking modal's dual-time line follows form edits anyway). */
export const useClock = (): Date => {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(t)
  }, [])
  return now
}

/* ── CACHED-OFFSET CLOCK ENGINE (Lee, 23 Sep 2026) ───────────────────────────
 * Intl.DateTimeFormat is the expensive part, so we call it ONCE per zone every
 * two hours to learn the zone's UTC offset, then run every clock locally with
 * plain date arithmetic. Display is MINUTES (HH:MM) — the seconds still exist
 * in the arithmetic, they're just not painted. One shared minute-aligned timer
 * writes textContent directly into every registered span: no React re-renders,
 * however many clocks are on screen. */
const OFFSET_TTL = 2 * 3600_000
const offsetCache = new Map<string, { off: number; at: number }>()

/** The zone's current UTC offset in minutes, cached for 2h. */
export const tzOffsetMin = (tz: string): number => {
  const hit = offsetCache.get(tz)
  if (hit && Date.now() - hit.at < OFFSET_TTL) return hit.off
  let off = 0
  const now = new Date()
  try {
    // Compare the zone's wall-clock (via formatToParts) with UTC, ± a day.
    const p = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', day: '2-digit', hourCycle: 'h23' }).formatToParts(now)
    const get = (t: string) => Number(p.find((x) => x.type === t)?.value || 0)
    let diff = get('hour') * 60 + get('minute') - (now.getUTCHours() * 60 + now.getUTCMinutes())
    const dayDiff = get('day') - now.getUTCDate()
    if (dayDiff === 1 || dayDiff < -1) diff += 1440
    else if (dayDiff === -1 || dayDiff > 1) diff -= 1440
    off = diff
  } catch { off = 0 }
  offsetCache.set(tz, { off, at: Date.now() })
  return off
}

/** HH:MM in the zone using the cached offset — no Intl call on the hot path. */
export const timeFromOffset = (tz: string, at: Date = new Date()): string => {
  const t = new Date(at.getTime() + tzOffsetMin(tz) * 60000)
  return `${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}`
}

type ClockEntry = { el: HTMLElement; tz: string }
const clockRegistry = new Set<ClockEntry>()
let clockTimer: ReturnType<typeof setTimeout> | null = null

const paintClocks = () => {
  const now = new Date()
  clockRegistry.forEach((e) => {
    if (!e.el.isConnected) { clockRegistry.delete(e); return }
    e.el.textContent = timeFromOffset(e.tz, now)
  })
  clockTimer = clockRegistry.size ? setTimeout(paintClocks, 60_000 - (Date.now() % 60_000) + 50) : null
}

/** Attach a live minute-ticking clock to a span. Returns the useEffect cleanup. */
export const attachLiveClock = (el: HTMLElement | null, tz: string): (() => void) => {
  if (!el) return () => {}
  const entry: ClockEntry = { el, tz }
  clockRegistry.add(entry)
  el.textContent = timeFromOffset(tz)
  if (!clockTimer) clockTimer = setTimeout(paintClocks, 60_000 - (Date.now() % 60_000) + 50)
  return () => { clockRegistry.delete(entry) }
}
